import React, { useMemo } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useTheme } from "../../hooks/useTheme";
import styles from "./ImageGallery.module.scss";
import { getImageUri } from "../../utils/functions/getImageUri";

type Props = {
  images: string[];
  posterImage?: string;
};

const ImageGallery = ({ images, posterImage }: Props) => {
  const { theme } = useTheme();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);
  const videoRefs = React.useRef<{ [key: number]: HTMLVideoElement | null }>(
    {},
  );

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  React.useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  React.useEffect(() => {
    Object.entries(videoRefs.current).forEach(([index, video]) => {
      if (!video) return;
      const isActive = Number(index) === selectedIndex;
      if (isActive) {
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [selectedIndex]);

  // Фильтруем постеры и сортируем: сначала видео, потом картинки
  const mediaFiles = useMemo(() => {
    const filtered = images.filter((path) => !path.includes("poster_"));
    const videos = filtered.filter((path) => path.includes("video_"));
    const imagesList = filtered.filter((path) => !path.includes("video_"));
    return [...videos, ...imagesList];
  }, [images]);

  const posterFile =
    posterImage || images.find((path) => path.includes("poster_"));

  if (!mediaFiles.length) return null;

  const renderMedia = (path: string, index: number) => {
    const url = `${getImageUri()}${path}`;
    const isVideo = path.includes("video_");

    if (isVideo) {
      const posterUrl = posterFile
        ? `${getImageUri()}${posterFile}`
        : undefined;
      return (
        <video
          ref={(el) => {
            if (el) videoRefs.current[index] = el;
          }}
          src={url}
          poster={posterUrl}
          className={styles.video}
          controls
          muted
          playsInline
          loop={false}
        />
      );
    }

    return <img src={url} alt={`Фото ${index + 1}`} className={styles.image} />;
  };

  return (
    <div className={styles.container}>
      <div className={styles.viewport} ref={emblaRef}>
        <div className={styles.container}>
          {mediaFiles.map((path, index) => (
            <div className={styles.slide} key={index}>
              {renderMedia(path, index)}
            </div>
          ))}
        </div>
      </div>

      {scrollSnaps.length > 1 && (
        <div className={styles.dots}>
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={`${styles.dot} ${index === selectedIndex ? styles.dotActive : ""}`}
              onClick={() => emblaApi?.scrollTo(index)}
              style={{
                backgroundColor:
                  index === selectedIndex
                    ? theme.onPrimary
                    : `${theme.onPrimary}80`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageGallery;